/**
 * <kz-seatmap> — KaartZeker interactive seat map (Horizon 4.2). Markup: snippets/kz-seatmap.liquid.
 *
 *  - hover / focus a block: tooltip ("Vak 204 · 2e ring · vanaf €150 · 2 tickets", sold: "Uitverkocht");
 *  - click / tap / Enter / Space on an open block: selected, panel filtered to that block
 *    ("Toon alle vakken" resets); a block with a single offer also selects that variant;
 *  - hovering a panel row highlights its block (two-way);
 *  - "Bekijk" / "Kies" (data-kz-select="<variantId>") selects the variant: through Horizon's
 *    <variant-picker> when present (check the radio / option with data-variant-id and dispatch a
 *    bubbling `change`), else by writing the form's input[name="id"] and ?variant=;
 *  - zoom: +/- buttons, pinch, and drag to pan while zoomed (transform on the canvas).
 */
class KzSeatmap extends HTMLElement {
  #scale = 1;
  #tx = 0;
  #ty = 0;
  #pointers = new Map();
  #pinch = null;
  #dragged = false;

  connectedCallback() {
    this.map = this.querySelector('[data-kz-map]');
    this.viewport = this.querySelector('[data-kz-viewport]');
    this.canvas = this.querySelector('[data-kz-canvas]');
    this.tip = this.querySelector('[data-kz-tip]');
    this.panel = this.querySelector('[data-kz-panel]');
    this.list = this.querySelector('[data-kz-list]');
    this.zoomIn = this.querySelector('[data-kz-zoom-in]');
    this.zoomOut = this.querySelector('[data-kz-zoom-out]');
    this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerover', this.#onOver);
    this.addEventListener('pointerout', this.#onOut);
    this.addEventListener('focusin', this.#onOver);
    this.addEventListener('focusout', this.#onOut);
    document.addEventListener('change', this.#onExternalChange);

    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', this.#onPointerDown);
      this.viewport.addEventListener('pointermove', this.#onPointerMove);
      this.viewport.addEventListener('pointerup', this.#onPointerUp);
      this.viewport.addEventListener('pointercancel', this.#onPointerUp);
    }

    const initial = new URL(window.location.href).searchParams.get('variant');
    if (initial) this.#highlightVariant(initial);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onExternalChange);
  }

  /* ------------------------------------------------------------ helpers */

  #segs() {
    return this.map ? Array.from(this.map.querySelectorAll('.kz-seg')) : [];
  }

  #items() {
    return this.list ? Array.from(this.list.querySelectorAll('[data-kz-item]')) : [];
  }

  /** Panel rows of a block; "BALKON" also owns "BALKON-L" etc. */
  #itemsFor(vak) {
    return this.#items().filter((i) => i.dataset.vak === vak || i.dataset.vak.startsWith(`${vak}-`));
  }

  #segFor(vak) {
    return this.#segs().find((g) => g.dataset.vak === vak || String(vak).startsWith(`${g.dataset.vak}-`)) || null;
  }

  /* ------------------------------------------------------------ click / keyboard */

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-kz-zoom-in]')) return this.#zoomBy(1.5);
    if (target.closest('[data-kz-zoom-out]')) return this.#zoomBy(1 / 1.5);

    const selectBtn = target.closest('[data-kz-select]');
    if (selectBtn) {
      const item = selectBtn.closest('[data-kz-item]');
      const isListing = Boolean(selectBtn.closest('.kz-panel__variant'));
      // "Bekijk" on a block row opens the block; "Kies" on a listing (fallback mode) selects it.
      if (item && !isListing && !this.classList.contains('kz-seatmap--detail')) {
        this.openBlock(item.dataset.vak, selectBtn.getAttribute('data-kz-select'));
        return;
      }
      if (item) this.#activateItem(item);
      this.selectVariant(selectBtn.getAttribute('data-kz-select'));
      return;
    }
    if (target.closest('[data-kz-reset]')) return this.clearFilter();

    const seg = target.closest('.kz-seg');
    if (seg && this.map?.contains(seg)) {
      if (this.#dragged) return; // the gesture was a pan, not a tap
      return this.#onSegActivate(seg);
    }
    const item = target.closest('[data-kz-item]');
    if (item && !target.closest('button') && !this.classList.contains('kz-seatmap--detail')) {
      this.openBlock(item.dataset.vak);
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') return this.#hideTip();
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const seg = /** @type {HTMLElement} */ (event.target).closest?.('.kz-seg');
    if (!seg) return;
    event.preventDefault();
    this.#onSegActivate(seg);
  };

  #onSegActivate(seg) {
    const { state, vak } = seg.dataset;
    if (state !== 'open') return;
    if (seg.getAttribute('aria-pressed') === 'true') return this.clearFilter();
    this.openBlock(vak);
  }

  /** A variant was chosen in Horizon's picker — mirror it on the map. */
  #onExternalChange = (event) => {
    const el = /** @type {HTMLElement} */ (event.target);
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
    if (!el.closest('variant-picker')) return;
    const id = el instanceof HTMLSelectElement ? el.options[el.selectedIndex]?.dataset.variantId : el.dataset.variantId;
    if (id) this.#highlightVariant(id);
  };

  /* ------------------------------------------------------------ hover: tooltip + two-way highlight */

  #onOver = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest) return;
    const seg = target.closest('.kz-seg');
    if (seg && this.map?.contains(seg)) {
      if (seg.dataset.state === 'dead') return;
      this.#showTip(seg);
      this.#itemsFor(seg.dataset.vak).forEach((i) => i.classList.add('is-hover'));
      return;
    }
    const item = target.closest('[data-kz-item]');
    if (item) this.#segFor(item.dataset.vak)?.classList.add('is-hover');
  };

  #onOut = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest) return;
    const seg = target.closest('.kz-seg');
    if (seg) {
      this.#hideTip();
      this.#items().forEach((i) => i.classList.remove('is-hover'));
      return;
    }
    if (target.closest('[data-kz-item]')) this.#segs().forEach((g) => g.classList.remove('is-hover'));
  };

  #tipText(seg) {
    const { vak, ring, state } = seg.dataset;
    const items = this.#itemsFor(vak);
    const name = seg.getAttribute('aria-label')?.split(',')[0] || vak;
    const parts = [name];
    if (ring && ring.toLowerCase() !== name.toLowerCase()) parts.push(ring);
    if (state === 'sold' || !items.length) {
      parts.push(this.dataset.strSold || 'Uitverkocht');
    } else {
      parts.push(`${this.dataset.strFrom || 'vanaf'} ${items[0].dataset.price}`);
      if (items.length === 1 && items[0].dataset.qty) parts.push(items[0].dataset.qty);
    }
    return parts.join(' · ');
  }

  #showTip(seg) {
    if (!this.tip || !this.viewport) return;
    this.tip.textContent = this.#tipText(seg);
    this.tip.hidden = false;
    const box = this.viewport.getBoundingClientRect();
    const r = seg.getBoundingClientRect();
    const tw = this.tip.offsetWidth;
    const th = this.tip.offsetHeight;
    let x = r.left + r.width / 2 - box.left - tw / 2;
    x = Math.max(4, Math.min(x, box.width - tw - 4));
    let y = r.top - box.top - th - 8;
    if (y < 4) y = Math.min(r.bottom - box.top + 8, box.height - th - 4);
    this.tip.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  #hideTip() {
    if (this.tip) this.tip.hidden = true;
  }

  /* ------------------------------------------------------------ selection state */

  #pressSeg(vak) {
    this.#segs().forEach((g) => {
      if (!g.hasAttribute('aria-pressed')) return;
      const hit = g.dataset.vak === vak || String(vak).startsWith(`${g.dataset.vak}-`);
      g.setAttribute('aria-pressed', hit ? 'true' : 'false');
    });
  }

  /**
   * Detail mode for one block: hides the list, shows "← Alle vakken" + the block heading and the
   * [data-kz-panel-detail] container, and dispatches `kz:vak-select` (see docs/SEATMAP.md).
   * When nothing fills the container, falls back to this element's own listing rows.
   * @param {string} vak
   * @param {string | null} [fallbackVariant] variant to select in fallback mode
   */
  openBlock(vak, fallbackVariant = null) {
    const seg = this.#segFor(vak);
    const rootVak = seg?.dataset.vak || vak;
    const hits = this.#itemsFor(rootVak);
    if (!hits.length) return;
    this.#pressSeg(rootVak);
    this.#items().forEach((item) => {
      item.hidden = !hits.includes(item);
    });
    this.#activateItem(hits[0]);

    const label = hits.length === 1 ? hits[0].dataset.title : seg?.getAttribute('aria-label')?.split(',')[0] || rootVak;
    const sub = hits.length === 1 ? hits[0].dataset.sub : seg?.dataset.ring || '';
    const head = this.querySelector('[data-kz-detail-head]');
    const detail = this.querySelector('[data-kz-panel-detail]');
    const title = this.querySelector('[data-kz-detail-title]');
    const subEl = this.querySelector('[data-kz-detail-sub]');
    if (title) title.textContent = label || '';
    if (subEl) subEl.textContent = sub || '';
    if (head) head.hidden = false;
    if (detail) detail.hidden = false;
    this.classList.add('kz-seatmap--filtered', 'kz-seatmap--detail');
    this.classList.remove('kz-seatmap--fallback');
    this.#activateItem(hits[0]);
    hits.slice(1).forEach((i) => i.querySelector('.kz-panel__variants')?.removeAttribute('hidden'));

    const listings = hits.flatMap((i) =>
      Array.from(i.querySelectorAll('.kz-panel__variant')).map((v) => ({ id: v.dataset.variantId, key: v.dataset.sort }))
    );
    listings.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const variantIds = listings.map((v) => v.id);
    this.dispatchEvent(
      new CustomEvent('kz:vak-select', {
        bubbles: true,
        detail: { vak: rootVak, ring: seg?.dataset.ring || '', label: label || '', sub: sub || '', variantIds },
      })
    );

    // Nobody rendered listing cards into the detail container: show our own rows instead.
    setTimeout(() => {
      if (!this.classList.contains('kz-seatmap--detail')) return;
      if (detail && detail.childElementCount > 0) return;
      this.classList.add('kz-seatmap--fallback');
      const pick = fallbackVariant || (variantIds.length === 1 ? variantIds[0] : null);
      // A real click, so page scripts that listen for clicks on [data-kz-select] follow along.
      const btn = pick && this.list?.querySelector(`.kz-panel__variant [data-kz-select="${pick}"]`);
      if (btn) btn.click();
      else if (pick) this.selectVariant(pick);
    }, 60);
  }

  /** Back to list mode. Dispatches `kz:vak-clear`. */
  clearFilter() {
    const wasDetail = this.classList.contains('kz-seatmap--detail');
    this.#items().forEach((item) => {
      item.hidden = false;
      item.classList.remove('is-active');
      const rows = item.querySelector('.kz-panel__variants');
      if (rows) rows.hidden = true;
    });
    this.classList.remove('kz-seatmap--filtered', 'kz-seatmap--detail', 'kz-seatmap--fallback');
    const head = this.querySelector('[data-kz-detail-head]');
    const detail = this.querySelector('[data-kz-panel-detail]');
    if (head) head.hidden = true;
    if (detail) detail.hidden = true;
    this.#segs().forEach((g) => g.hasAttribute('aria-pressed') && g.setAttribute('aria-pressed', 'false'));
    if (wasDetail) this.dispatchEvent(new CustomEvent('kz:vak-clear', { bubbles: true }));
  }

  #activateItem(item) {
    this.list?.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    item.classList.add('is-active');
    const rows = item.querySelector('.kz-panel__variants');
    if (rows) rows.hidden = !this.classList.contains('kz-seatmap--detail');
  }

  #highlightVariant(id) {
    const item =
      this.list?.querySelector(`[data-kz-item][data-variant-id="${id}"]`) ||
      this.list?.querySelector(`.kz-panel__variant[data-variant-id="${id}"]`)?.closest('[data-kz-item]');
    if (!item) return;
    this.#activateItem(item);
    this.#pressSeg(item.dataset.vak);
    this.list?.querySelectorAll('.kz-panel__variant').forEach((row) => {
      row.classList.toggle('is-active', row.dataset.variantId === id);
    });
  }

  /* ------------------------------------------------------------ variant selection */

  #findPicker() {
    const pid = this.dataset.productId;
    const pickers = Array.from(document.querySelectorAll('variant-picker')).filter(
      (p) => !p.closest('product-card') && !p.closest('quick-add-dialog')
    );
    return pickers.find((p) => p.dataset.productId === pid) || pickers[0] || null;
  }

  /** @param {string | null} id */
  selectVariant(id) {
    if (!id) return;
    const picker = this.#findPicker();
    let handled = false;
    if (picker) {
      const radio = /** @type {HTMLInputElement|null} */ (picker.querySelector(`input[data-variant-id="${id}"]`));
      const option = /** @type {HTMLOptionElement|null} */ (picker.querySelector(`option[data-variant-id="${id}"]`));
      const select = option?.closest('select');
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        handled = true;
      } else if (option && select) {
        if (select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        handled = true;
      }
    }
    if (!handled) {
      document
        .querySelectorAll('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]')
        .forEach((input) => {
          if (input.closest('product-card, quick-add-dialog')) return;
          input.value = id;
        });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      history.replaceState({}, '', url.toString());
    }

    this.#highlightVariant(id);
    this.#announce(this.dataset.strSelected || 'Tickets geselecteerd.');

    if (window.matchMedia('(max-width: 989px)').matches) {
      const buy = document.querySelector('product-form-component, form[action*="/cart/add"]');
      // Let the page's own script render the choice first.
      setTimeout(() => buy?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' }), 60);
    }
  }

  /* ------------------------------------------------------------ zoom / pan */

  #applyTransform() {
    if (!this.canvas || !this.viewport) return;
    const w = this.viewport.clientWidth;
    const h = this.viewport.clientHeight;
    this.#scale = Math.max(1, Math.min(4, this.#scale));
    this.#tx = Math.min(0, Math.max(w - w * this.#scale, this.#tx));
    this.#ty = Math.min(0, Math.max(h - h * this.#scale, this.#ty));
    this.canvas.style.transform = `translate(${this.#tx}px, ${this.#ty}px) scale(${this.#scale})`;
    this.viewport.classList.toggle('is-zoomed', this.#scale > 1.01);
    if (this.zoomOut) this.zoomOut.disabled = this.#scale <= 1.01;
    if (this.zoomIn) this.zoomIn.disabled = this.#scale >= 3.99;
    this.#hideTip();
  }

  /** Zoom by `factor` around a point in viewport coordinates (default: centre). */
  #zoomBy(factor, px, py) {
    if (!this.viewport) return;
    const cx = px ?? this.viewport.clientWidth / 2;
    const cy = py ?? this.viewport.clientHeight / 2;
    const next = Math.max(1, Math.min(4, this.#scale * factor));
    const k = next / this.#scale;
    this.#tx = cx - (cx - this.#tx) * k;
    this.#ty = cy - (cy - this.#ty) * k;
    this.#scale = next;
    this.#applyTransform();
  }

  #onPointerDown = (event) => {
    if (event.target.closest('[data-kz-zoom]')) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#dragged = false;
    if (this.#pointers.size === 2) {
      const [a, b] = Array.from(this.#pointers.values());
      this.#pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y) };
    }
  };

  #onPointerMove = (event) => {
    const prev = this.#pointers.get(event.pointerId);
    if (!prev) return;
    const cur = { x: event.clientX, y: event.clientY };
    this.#pointers.set(event.pointerId, cur);
    const box = this.viewport.getBoundingClientRect();
    if (this.#pointers.size === 2 && this.#pinch) {
      const [a, b] = Array.from(this.#pointers.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.#pinch.dist > 0) {
        this.#zoomBy(dist / this.#pinch.dist, (a.x + b.x) / 2 - box.left, (a.y + b.y) / 2 - box.top);
      }
      this.#pinch.dist = dist;
      this.#dragged = true;
    } else if (this.#pointers.size === 1 && this.#scale > 1.01 && (event.buttons & 1 || event.pointerType !== 'mouse')) {
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) this.#dragged = true;
      this.#tx += dx;
      this.#ty += dy;
      this.#applyTransform();
    }
  };

  #onPointerUp = (event) => {
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size < 2) this.#pinch = null;
    // #dragged is read by the click that follows this pointerup, then cleared.
    if (this.#dragged) setTimeout(() => (this.#dragged = false), 0);
  };

  #announce(text) {
    let region = this.querySelector('[data-kz-live]');
    if (!region) {
      region = document.createElement('div');
      region.setAttribute('data-kz-live', '');
      region.setAttribute('aria-live', 'polite');
      region.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
      this.appendChild(region);
    }
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = text;
    });
  }
}

if (!customElements.get('kz-seatmap')) {
  customElements.define('kz-seatmap', KzSeatmap);
}

export { KzSeatmap };
